import { computed, toValue, type MaybeRefOrGetter } from 'vue'

export interface PageSeo {
  title: MaybeRefOrGetter<string>
  description: MaybeRefOrGetter<string>
  /** Same-origin path or absolute URL of the share image. */
  image?: MaybeRefOrGetter<string | null | undefined>
}

/** Absolute URL on the current tenant origin; search and social crawlers ignore relative ones. */
export function useAbsoluteUrl() {
  const origin = useRequestURL().origin
  return (path: string) => new URL(path, origin).href
}

/** Page title and description mirrored to Open Graph/Twitter; app.vue owns the site-wide defaults. */
export function usePageSeo(seo: PageSeo) {
  const { t } = useI18n()
  const absoluteUrl = useAbsoluteUrl()
  const socialTitle = () => `${toValue(seo.title)} · ${t('public.brand')}`
  const image = () => {
    const path = toValue(seo.image)
    return path ? absoluteUrl(path) : undefined
  }

  useSeoMeta({
    title: seo.title,
    description: seo.description,
    ogTitle: socialTitle,
    ogDescription: seo.description,
    twitterTitle: socialTitle,
    twitterDescription: seo.description,
    ...(seo.image
      ? {
          ogImage: image,
          twitterImage: image,
          twitterCard: () => (image() ? 'summary_large_image' : 'summary')
        }
      : {})
  })
}

/** Serializes structured data so tenant-authored text can never close the script element. */
export function serializeJsonLd(data: unknown) {
  return JSON.stringify(data).replace(/</g, '\\u003c')
}

export function useJsonLd(data: MaybeRefOrGetter<Record<string, unknown> | null>) {
  useHead({
    script: computed(() => {
      const value = toValue(data)
      return value ? [{ key: 'json-ld', type: 'application/ld+json', innerHTML: serializeJsonLd(value) }] : []
    })
  })
}
